import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FilePdf, ArrowsClockwise, Warning } from "@phosphor-icons/react";
import { ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { api, apiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOpsProjects, getOpsProject } from "@/components/OpsProjectSelector";

const n1 = (v, suf = "") => (v == null ? "—" : `${Number(v).toLocaleString("es-MX", { maximumFractionDigits: 1 })}${suf}`);
const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const today = () => new Date().toISOString().slice(0, 10);
const STATUS = { ok: "bg-emerald-100 text-emerald-800", warn: "bg-amber-100 text-amber-800", bad: "bg-rose-100 text-rose-800" };
const STATUS_L = { ok: "EN META", warn: "ATENCIÓN", bad: "CRÍTICO" };

function Kpi({ label, value, sub, tone = "slate", testid }) {
  const tones = { slate: "border-slate-200", good: "border-emerald-400", warn: "border-amber-400", bad: "border-rose-400", navy: "border-blue-900 bg-blue-900 text-white" };
  return (
    <div className={`border-2 rounded-md p-2.5 bg-white ${tones[tone]}`} data-testid={testid}>
      <p className={`text-[9px] uppercase tracking-widest font-bold ${tone === "navy" ? "text-blue-100" : "text-slate-500"}`}>{label}</p>
      <p className="font-heading font-black text-lg leading-tight">{value}</p>
      {sub && <p className={`text-[10px] ${tone === "navy" ? "text-blue-100" : "text-slate-500"}`}>{sub}</p>}
    </div>
  );
}

function Block({ title, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid={testid}>
      <h3 className="font-heading font-bold text-blue-900 uppercase text-[11px] tracking-widest mb-2">{title}</h3>
      {children}
    </div>
  );
}

const varTone = (p) => (p == null ? "slate" : p >= -5 ? "good" : p >= -15 ? "warn" : "bad");

export default function OperationsPeriodSummary() {
  const projects = useOpsProjects();
  const activeProj = getOpsProject();
  const scopeLabel = activeProj ? (projects.find((p) => p.id === activeProj)?.name || "Proyecto") : "Todos los proyectos (general)";
  const [range, setRange] = useState({ date_from: firstOfMonth(), date_to: today() });
  const [projTo, setProjTo] = useState("");
  const [planMode, setPlanMode] = useState("monthly");
  const setMode = (m) => {
    setPlanMode(m);
    if (m === "weekly") { const d = new Date(); const day = d.getDay() || 7; const mon = new Date(d); mon.setDate(d.getDate() - day + 1); const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      setRange({ date_from: mon.toISOString().slice(0, 10), date_to: sun.toISOString().slice(0, 10) }); }
    else setRange({ date_from: firstOfMonth(), date_to: today() });
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/operations/period-summary", { params: { ...range, plan_mode: planMode, projection_to: projTo || undefined } }); setData(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [range, planMode, projTo]);
  useEffect(() => { load(); }, [load]);

  const chartSeries = useMemo(() => {
    if (!data) return [];
    const byDate = new Map((data.series || []).map((s) => [s.date, { ...s }]));
    (data.projection || []).forEach((p) => {
      const ex = byDate.get(p.date) || { date: p.date };
      ex.projection = p.projection;
      if (p.plan_cumulative != null) ex.plan_cumulative = p.plan_cumulative;
      byDate.set(p.date, ex);
    });
    return Array.from(byDate.values()).sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [data]);

  const pdf = async () => {
    try { await downloadFile(`/operations/period-summary/pdf?date_from=${range.date_from}&date_to=${range.date_to}&plan_mode=${planMode}${projTo ? `&projection_to=${projTo}` : ""}`, `resumen-operacion-${range.date_from}-a-${range.date_to}.pdf`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const m = data?.meters, t = data?.tonnage, d = data?.drilling_blasting, s = data?.support, h = data?.hours;

  return (
    <div className="space-y-4" data-testid="ops-period-summary">
      <div className="flex flex-wrap items-end gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <div className="flex rounded-md overflow-hidden border border-blue-900" data-testid="ops-ps-mode">
          {[["monthly", "Plan Mensual"], ["weekly", "Plan Semanal"]].map(([m, l]) => (
            <button key={m} onClick={() => setMode(m)} className={`h-8 px-3 text-xs font-bold ${planMode === m ? "bg-blue-900 text-white" : "bg-white text-blue-900"}`} data-testid={`ops-ps-mode-${m}`}>{l}</button>
          ))}
        </div>
        <div><Label className="text-[10px] uppercase">Desde</Label><Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="h-8" data-testid="ops-ps-from" /></div>
        <div><Label className="text-[10px] uppercase">Hasta</Label><Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="h-8" data-testid="ops-ps-to" /></div>
        <div><Label className="text-[10px] uppercase text-blue-900">Proyectar hasta</Label><Input type="date" value={projTo} min={range.date_to} onChange={(e) => setProjTo(e.target.value)} className="h-8 border-blue-300" data-testid="ops-ps-projto" /></div>
        <Button size="sm" variant="outline" onClick={load} className="h-8 gap-1" data-testid="ops-ps-refresh"><ArrowsClockwise size={13} /> Actualizar</Button>
        <Button size="sm" onClick={pdf} className="h-8 gap-1 bg-blue-900 hover:bg-blue-800 text-white" data-testid="ops-ps-pdf"><FilePdf size={13} /> PDF ejecutivo</Button>
        <span className="text-[10px] font-bold px-2 py-1 rounded bg-blue-900 text-white" data-testid="ops-ps-scope">Alcance: {scopeLabel}</span>
        {data && <span className="text-[11px] text-slate-500 ml-auto">{data.period.elapsed_days}/{data.period.total_days} días · {data.period.shifts_captured} turnos · {data.period.entries} capturas</span>}
      </div>

      {loading && !data && <p className="text-center text-slate-400 text-sm py-10">Calculando…</p>}
      {data && (
        <>
          {data.alerts.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 space-y-0.5" data-testid="ops-ps-alerts">
              <p className="text-[10px] uppercase font-bold text-rose-700 flex items-center gap-1"><Warning size={12} weight="fill" /> Alertas de gestión ({data.alerts.length})</p>
              {data.alerts.map((a, i) => <p key={i} className="text-[11px] text-rose-800">• {a}</p>)}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2" data-testid="ops-ps-kpis">
            <Kpi label="Avance real" value={n1(m.real, " m")} sub={`${n1(m.m_per_shift)} m/turno`} tone="navy" testid="ops-ps-kpi-real" />
            <Kpi label={planMode === "weekly" ? "Plan a la fecha (sem.)" : "Plan MTD"} value={n1(m.plan_mtd, " m")} sub={`de ${n1(m.plan_period)} m ${planMode === "weekly" ? "plan semanal" : "período"}`} />
            <Kpi label="Var. MTD" value={n1(m.variance_mtd_pct, "%")} sub="real vs plan prorrateado" tone={varTone(m.variance_mtd_pct)} testid="ops-ps-kpi-var" />
            <Kpi label="Cumplimiento" value={n1(m.compliance_pct, "%")} sub="real / plan período" tone={m.compliance_pct == null ? "slate" : m.compliance_pct >= 95 ? "good" : m.compliance_pct >= 80 ? "warn" : "bad"} />
            <Kpi label="Forecast cierre" value={n1(m.forecast, " m")} sub={`${n1(m.forecast_pct, "%")} del plan · ${n1(m.rate_m_day)} m/día`} tone={varTone(m.forecast_pct == null ? null : m.forecast_pct - 100)} testid="ops-ps-kpi-forecast" />
            <Kpi label="Toneladas" value={n1(t.total, " t")} sub={`Min ${n1(t.mineral)} · Tep ${n1(t.tepetate)}`} />
            <Kpi label="Disponibilidad" value={n1(h.availability_pct, "%")} sub={`${n1(h.total)} h · ${n1(h.delays)} h demora`} tone={h.availability_pct == null ? "slate" : h.availability_pct >= 85 ? "good" : "warn"} />
            <Kpi label="Utilización" value={n1(data.time_studies.utilization_pct, "%")} sub={`${data.time_studies.count} estudios de tiempos`} />
          </div>

          <div className="grid xl:grid-cols-[1.4fr_1fr] gap-3">
            <Block title={`Curva de avance · real acumulado vs plan${data.projection_to ? ` · proyección a ${data.projection_to}` : ""}`} testid="ops-ps-chart">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartSeries} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 9 }} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="real" name="Diario" fill="#94a3b8" />
                    <Line type="monotone" dataKey="plan_cumulative" name="Plan acum." stroke="#b91c1c" strokeDasharray="5 4" dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="cumulative" name="Real acum." stroke="#1e3a8a" dot={false} strokeWidth={2.5} connectNulls={false} />
                    <Line type="monotone" dataKey="projection" name="Proyección" stroke="#2563eb" strokeDasharray="2 3" dot={false} strokeWidth={1.8} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Block>
            <Block title="Plan vs real por obra" testid="ops-ps-fronts">
              <div className="overflow-auto max-h-56">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-100 sticky top-0"><tr>{["Obra", "Plan MTD", "Real", "Var.", "Forecast", "Estado"].map((x) => <th key={x} className="px-1.5 py-1 text-left font-semibold">{x}</th>)}</tr></thead>
                  <tbody>
                    {data.fronts.map((f) => (
                      <tr key={f.front_name} className="border-t" data-testid={`ops-ps-front-${f.front_name}`}>
                        <td className="px-1.5 py-1 font-semibold">{f.front_name}</td>
                        <td className="px-1.5 py-1">{n1(f.plan_mtd)}</td>
                        <td className="px-1.5 py-1 font-bold">{n1(f.real)}</td>
                        <td className="px-1.5 py-1">{n1(f.variance_mtd_pct, "%")}</td>
                        <td className="px-1.5 py-1">{n1(f.forecast)} <span className="text-slate-400">/ {n1(f.plan_period)}</span></td>
                        <td className="px-1.5 py-1"><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${STATUS[f.status]}`}>{STATUS_L[f.status]}</span></td>
                      </tr>
                    ))}
                    {data.fronts.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-4">Sin obras en el período.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Block>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <Block title="Acarreo y toneladas" testid="ops-ps-tonnage">
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                {[["Mineral", n1(t.mineral, " t")], ["Tepetate", n1(t.tepetate, " t")], ["Budget mineral", n1(t.budget_mineral, " t")], ["Cumplim. mineral", n1(t.compliance_mineral_pct, "%")],
                  ["Scoop", n1(t.scoop, " t")], ["Camión", n1(t.camion, " t")], ["Viajes camión", t.viajes], ["Cucharones", t.cucharones],
                  ["t por m avance", n1(t.ton_per_m)], ["t/h scoop", n1(t.ton_per_scoop_hour)]].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100"><dt className="text-slate-500">{k}</dt><dd className="font-semibold">{v}</dd></div>
                ))}
              </dl>
            </Block>
            <Block title="Perforación, voladura y sostenimiento" testid="ops-ps-drill">
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                {[["Barrenos perforados", d.barrenos_perforados], ["Barrenos disparados", d.barrenos_disparados], ["Metros perforados", n1(d.metros_perforados, " m")], ["Explosivo", n1(d.explosivo_kg, " kg")],
                  ["Factor de carga", n1(d.powder_factor_kg_m, " kg/m")], ["Encapille", n1(d.encapille_m, " m")], ["Anclas", s.pernos], ["Anclas por m", n1(s.pernos_per_m)],
                  ["Malla", n1(s.malla_m2, " m²")], ["Diésel", n1(h.diesel_l, " L")]].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100"><dt className="text-slate-500">{k}</dt><dd className="font-semibold">{v}</dd></div>
                ))}
              </dl>
            </Block>
            <Block title="Equipos · horas y disponibilidad" testid="ops-ps-equipment">
              <table className="w-full text-[11px]">
                <thead><tr className="text-slate-500 text-[9px] uppercase"><th className="text-left">Equipo</th><th>Turnos</th><th>Horas</th><th>Demora</th><th>Disp.</th></tr></thead>
                <tbody>
                  {data.equipment.map((e) => (
                    <tr key={e.type} className="border-t text-center"><td className="text-left font-semibold">{e.label}</td><td>{e.shifts}</td><td>{n1(e.hours)}</td><td>{n1(e.delay_hours)}</td>
                      <td className={e.availability_pct != null && e.availability_pct < 80 ? "text-rose-700 font-bold" : ""}>{n1(e.availability_pct, "%")}</td></tr>
                  ))}
                  {data.equipment.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-3">Sin capturas.</td></tr>}
                </tbody>
              </table>
              <p className="text-[10px] text-slate-500 mt-2">HSE: {data.hse.incidents} incidente(s), {data.hse.high_severity} de severidad alta.</p>
            </Block>
          </div>
        </>
      )}
    </div>
  );
}
